/**
 * Portal Discount Options API Client
 *
 * Handles operations for discount options management with Cognito authentication.
 */

// API Gateway URL
const API_BASE_URL = 'https://jrgfnay5w2.execute-api.ap-southeast-2.amazonaws.com/v1';

/**
 * Get the authentication token from the user's session.
 */
const getAuthToken = () => {
  const oidcStorage = sessionStorage.getItem('oidc.user:https://cognito-idp.ap-southeast-2.amazonaws.com/ap-southeast-2_3n9j6gDzL:2c6na652k2lkcsaa1ol8m5chp4');
  if (oidcStorage) {
    try {
      const oidcData = JSON.parse(oidcStorage);
      return oidcData.id_token || oidcData.access_token;
    } catch (e) {
      console.error('Error parsing OIDC storage:', e);
    }
  }
  return null;
};

/**
 * Make an authenticated API request.
 */
const apiRequest = async (operation, data = {}) => {
  const token = getAuthToken();

  if (!token) {
    throw new Error('Not authenticated. Please log in again.');
  }

  const response = await fetch(`${API_BASE_URL}/discount-options-admin`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    },
    body: JSON.stringify({
      operation,
      data
    })
  });

  const result = await response.json();

  if (!response.ok) {
    throw new Error(result.error || `Request failed with status ${response.status}`);
  }

  return result;
};

/**
 * List discount options.
 *
 * @param {Object} filters - Optional filters
 * @param {string} filters.campusId - Filter by campus ID (or 'global' for global only)
 * @param {boolean} filters.showInactive - Include inactive discounts
 */
export const listDiscountOptions = async (filters = {}) => {
  const result = await apiRequest('list', filters);
  return result.discountOptions || [];
};

/**
 * Create a new discount option.
 *
 * @param {Object} data - Discount option data
 * @param {string} data.name - Discount name
 * @param {string} data.description - Optional description
 * @param {number} data.value - Discount percentage (0-100)
 * @param {string} data.campusId - Campus ID or 'global'
 */
export const createDiscountOption = async (data) => {
  const result = await apiRequest('create', data);
  return result.discountOption;
};

/**
 * Update a discount option.
 *
 * @param {string} id - Discount option ID
 * @param {Object} updates - Fields to update
 */
export const updateDiscountOption = async (id, updates) => {
  const result = await apiRequest('update', { id, ...updates });
  return result.discountOption;
};

/**
 * Delete a discount option (soft delete).
 *
 * @param {string} id - Discount option ID
 */
export const deleteDiscountOption = async (id) => {
  const result = await apiRequest('delete', { id });
  return result;
};

/**
 * Get discount options for a specific campus.
 * Returns both campus-specific and global discounts.
 *
 * @param {string} campusId - Campus ID
 */
export const getDiscountOptionsForCampus = async (campusId) => {
  const allOptions = await listDiscountOptions();
  return allOptions.filter(
    opt => opt.campusId === 'global' || opt.campusId === campusId
  );
};

const discountOptionsPortalApi = {
  listDiscountOptions,
  createDiscountOption,
  updateDiscountOption,
  deleteDiscountOption,
  getDiscountOptionsForCampus
};

export default discountOptionsPortalApi;
